import { useEffect, useState } from "react";
import { LANGS, langPath, APP_STORE_URL, PLAY_STORE_URL } from "../seo/site";
import Picture from "../Picture";
import { ANALYTICS_ENABLED } from "../analytics/config";
import { openConsentSettings } from "../analytics/consent";

/* "/" — the x-default page of the hreflang cluster. Lets visitors pick a
   language; it never redirects. The browser language is only used to
   highlight a suggestion after the page has loaded. */

const FONT =
  '"Poppins", "Poppins Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const CARDS = {
  sv: { line: "Plugga körkortsteori på svenska", cta: "Fortsätt", suggested: "Förslag" },
  en: { line: "Study Swedish driving theory in English", cta: "Continue", suggested: "Suggested" },
  uk: { line: "Вивчайте теорію водіння українською", cta: "Продовжити", suggested: "Рекомендовано" },
  ru: { line: "Изучайте теорию вождения на русском", cta: "Продолжить", suggested: "Рекомендуем" },
};

const CSS = `
.tk-ls { min-height: 100vh; margin: 0; background: #fafafa; color: #18181b; font-family: ${FONT}; display: flex; flex-direction: column; }
.tk-ls *, .tk-ls *::before, .tk-ls *::after { box-sizing: border-box; }
.tk-ls a { color: inherit; text-decoration: none; }
.tk-ls a:focus-visible { outline: 2px solid #18181b; outline-offset: 3px; }
.tk-ls-main { flex: 1; width: min(100% - 2rem, 46rem); margin: 0 auto; padding: 3rem 0 2rem; display: flex; flex-direction: column; align-items: center; text-align: center; }
.tk-ls-logo { width: 72px; height: 72px; border-radius: 18px; display: block; }
.tk-ls h1 { margin: 1.25rem 0 .5rem; font-size: 2.25rem; line-height: 1.1; font-weight: 800; letter-spacing: -.03em; }
.tk-ls-lead { margin: 0; color: #52525b; font-size: 1.05rem; line-height: 1.6; }
.tk-ls-prompt { margin: 2rem 0 1rem; color: #71717a; font-size: .85rem; font-weight: 600; letter-spacing: .02em; }
.tk-ls-grid { list-style: none; margin: 0; padding: 0; width: 100%; display: grid; gap: .75rem; grid-template-columns: 1fr; }
.tk-ls-card { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1.1rem 1.25rem; background: #fff; border: 1px solid #e4e4e7; border-radius: 1rem; text-align: left; transition: border-color .15s, box-shadow .15s; }
.tk-ls-card:hover { border-color: #a1a1aa; box-shadow: 0 10px 24px -14px rgba(0,0,0,.25); }
.tk-ls-card[data-suggested="true"] { border-color: #18181b; box-shadow: 0 0 0 1px #18181b; }
.tk-ls-name { display: block; font-size: 1.15rem; font-weight: 700; }
.tk-ls-line { display: block; margin-top: .2rem; color: #52525b; font-size: .9rem; line-height: 1.45; }
.tk-ls-cta { flex-shrink: 0; font-size: .85rem; font-weight: 700; color: #18181b; }
.tk-ls-badge { position: absolute; top: -.6rem; right: 1rem; padding: .1rem .55rem; border-radius: 999px; background: #fbbf24; color: #18181b; font-size: .7rem; font-weight: 700; }
.tk-ls-stores { margin-top: 2.5rem; display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: .75rem; }
.tk-ls-footer { padding: 1.5rem 1rem 2rem; display: flex; gap: 1.25rem; justify-content: center; color: #71717a; font-size: .8rem; }
.tk-ls-footer a:hover { color: #18181b; }
@media (min-width: 640px) {
  .tk-ls-main { padding-top: 5rem; }
  .tk-ls h1 { font-size: 3rem; }
  .tk-ls-grid { grid-template-columns: 1fr 1fr; }
}
`;

/* First supported language in the browser's list, or null. */
function browserLang() {
  try {
    const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
    for (const raw of list) {
      const code = String(raw).toLowerCase().split("-")[0];
      if (LANGS.some((l) => l.code === code)) return code;
    }
  } catch (e) { /* ignore */ }
  return null;
}

export default function LanguageSelect() {
  const [suggested, setSuggested] = useState(null);
  useEffect(() => { setSuggested(browserLang()); }, []);

  return (
    <div className="tk-ls">
      <style>{CSS}</style>
      <main className="tk-ls-main">
        <Picture className="tk-ls-logo" src="/images/icon.png" alt="Teorikörkort" sizes="72px" loading="eager" fetchPriority="high" />
        <h1>Teorikörkort</h1>
        <p className="tk-ls-lead">Appen för teoriprovet – körkort B. Frågor, vägmärken och teori på fyra språk.</p>
        <p className="tk-ls-prompt">
          <span lang="sv">Välj språk</span> · <span lang="en">Choose language</span> ·{" "}
          <span lang="uk">Оберіть мову</span> · <span lang="ru">Выберите язык</span>
        </p>
        <ul className="tk-ls-grid" data-track-placement="chooser">
          {LANGS.map((l) => {
            const c = CARDS[l.code];
            const isSuggested = suggested === l.code;
            return (
              <li key={l.code}>
                <a className="tk-ls-card" href={langPath(l.code)} hrefLang={l.code} lang={l.code} data-suggested={isSuggested ? "true" : undefined}>
                  {isSuggested ? <span className="tk-ls-badge">{c.suggested}</span> : null}
                  <span>
                    <span className="tk-ls-name">{l.native}</span>
                    <span className="tk-ls-line">{c.line}</span>
                  </span>
                  <span className="tk-ls-cta" aria-hidden="true">{c.cta} →</span>
                </a>
              </li>
            );
          })}
        </ul>
        <div className="tk-ls-stores" data-track-placement="chooser">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/images/Download_on_the_App_Store_Badge.svg" alt="Ladda ned i App Store" width="120" height="40" loading="lazy" style={{ height: 48, width: "auto", display: "block" }} />
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/images/google-play-badge.png" alt="Ladda ned på Google Play" width="646" height="250" loading="lazy" style={{ height: 70, width: "auto", display: "block", margin: "-11px" }} />
          </a>
        </div>
      </main>
      <footer className="tk-ls-footer">
        <a href="/privacy">Privacy Policy</a>
        <a href="/terms-of-service">Terms of Service</a>
        {ANALYTICS_ENABLED && (
          <button type="button" onClick={openConsentSettings} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", color: "inherit" }}>
            Cookie settings
          </button>
        )}
      </footer>
    </div>
  );
}
